import { Button } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useWallets } from "api/wallets";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { OLModal } from "components/Tooltips/OLModal";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { Field, Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { useState } from "react";
import { Dialog } from "./EffectDialog";

const useStyles = makeStyles((theme: OLTheme) => ({
  btnWrapper: {
    display: "flex",
    gap: theme.spacing(12),
  },
  modalButton: {
    marginTop: theme.spacing(12),
  },
  menuVisible: {
    overflowY: "visible",
  },
}));

interface Props {
  dialog: Dialog | null;
  isDialogOpen: boolean;
  closeDialog: () => void;
  initialValue?: string;
  setFieldValue: (value: string) => void;
  currentValue: string;
  multiInput: boolean;
}

const EffectWalletDialog = ({
  dialog,
  isDialogOpen,
  closeDialog,
  initialValue,
  setFieldValue,
  currentValue,
  multiInput,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const [walletTypeSearch, setWalletTypeSearch] = useState("");

  const { useInfiniteWalletsList } = useWallets();
  const {
    data: infiniteWalletsList,
    size: walletsSize,
    setSize: setWalletsSize,
    isValidating,
  } = useInfiniteWalletsList({
    filters: walletTypeSearch
      ? [
          {
            condition: "like",
            query: "name",
            value: walletTypeSearch,
          },
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ]
      : [
          {
            condition: "eq",
            query: "active",
            value: true,
          },
        ],
    order: "asc",
    sortKey: "name",
  });
  const { infiniteData: walletsList, anyElementsToFetch: anyWalletsToFetch } =
    useManageInfiniteScroll(infiniteWalletsList || []);

  return isDialogOpen && dialog ? (
    <OLModal
      header={dialog.title}
      onClose={() => closeDialog()}
      open={isDialogOpen}
      modalWidth={536}
      className={classes.menuVisible}
    >
      <Formik
        initialValues={{ key: initialValue || "" }}
        onSubmit={async (values) => {
          const injectedValue = dialog?.code.replace(
            "('')",
            `("${values.key}")`,
          );

          if (multiInput) {
            setFieldValue(`${currentValue} <#>${injectedValue}</#>`);
          } else {
            setFieldValue(`<#>${injectedValue}</#>`);
          }

          closeDialog();
        }}
      >
        {({ handleSubmit, isSubmitting }) => (
          <Form onSubmit={handleSubmit}>
            <Field name="key">
              {({ field }: { field: any }) => (
                <SelectInput
                  {...field}
                  displaySearch
                  data-test-id="wallet-type"
                  items={
                    walletsList?.map(({ code, name }) => ({
                      id: code,
                      label: name,
                    })) || []
                  }
                  label={
                    <RequiredFieldLabel text={message({ id: "walletType" })} />
                  }
                  asyncSearch={(searchedQuery) =>
                    setWalletTypeSearch(searchedQuery)
                  }
                  loadMoreElements={() =>
                    !isValidating && setWalletsSize(walletsSize + 1)
                  }
                  anyElementsToFetch={anyWalletsToFetch}
                />
              )}
            </Field>
            <div className={classes.btnWrapper}>
              <Button
                className={classes.modalButton}
                color="primary"
                disabled={isSubmitting}
                fullWidth
                type="button"
                variant="outlined"
                onClick={() => closeDialog()}
                data-test-id="dialog-cancel"
              >
                {message({ id: "cancel" })}
              </Button>
              <Button
                className={classes.modalButton}
                color="primary"
                disabled={isSubmitting}
                fullWidth
                type="submit"
                variant="contained"
                data-test-id="dialog-save"
              >
                {message({ id: "save" })}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </OLModal>
  ) : null;
};

export default EffectWalletDialog;
